import React, {createElement, useEffect, useState, Fragment} from 'react';
import Corner from './corner.svg';
import {Announcements, Activities, Collaboration, Clarity, Diary, Goals, Identity, ErrorBoundary} from 'components';
import {Notes, Lists, Redirect, Route, Settings, Switch, Today, Welcome, Test, TestRepetition} from 'components';
import {Topbar, Navigation, Loading} from '.';
import {config, cookies, useMedia, useLocation, useNamedSelector} from 'helpers';
import css from './layout.module.scss';

/**
 * The Manager component handles the main application layout and routing logic based on user session state.
 * It conditionally renders the appropriate layout (Loading, MainLayout, or Login).
 */
export function Manager(): JSX.Element {
  const user = useNamedSelector('session.user');
  const [Layout, setLayout] = useState<React.ComponentType>(() => Loading);

  useEffect(() => {
    const loadingLabel = document.getElementById('loading-label');
    if (user) {
      setLayout(() => MainLayout);
      if (loadingLabel) {
        loadingLabel.parentNode?.removeChild(loadingLabel);
      }
    } else if (config.development && !cookies.get('token')) {
      setLayout(() => Settings.Login);
    }
  }, [user]);

  return createElement(Layout);
}

/**
 * The MainLayout component renders the primary application layout and handles routing and navigation.
 * It includes top-level navigation, error boundaries, and conditional UI elements based on the user session.
 */
function MainLayout(): JSX.Element | null {
  const isDesktop = useMedia('(min-width: 62em)');
  const {user, subscription} = useNamedSelector('session.session');
  const {pathname} = useLocation();
  const {primaryNavigationCollapsed, printTarget} = useNamedSelector('settings.ui');

  useEffect(() => {
    window.document.body.classList.toggle('has-collapsed-primary-navigation', primaryNavigationCollapsed);
  }, [primaryNavigationCollapsed]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return Boolean(user) ? (
    <div className={css('layout', `print-${printTarget}`, {primaryNavigationCollapsed})}>
      <Topbar />
      <Navigation />
      <ErrorBoundary wrap={content => <main>{content}</main>}>
        <Switch>
          <Route path="/:module(activities|routines|habits)/:page?" component={Activities.Router} />
          <Route path="/diary/" component={Diary.Router} />
          <Route path="/goals/" component={Goals.Router} />
          <Route path="/notes/" component={Notes.Router} />
          {subscription.premium && <Route path="/lists/" component={Lists.Router} />}
          <Route path="/settings/" component={Settings.Router} />
          <Route path="/today/:action(tracking|timelog)?/:id?/" component={Today.Router} />
          <Route path="/today/" component={Today.Router} />
          <Route path="/welcome/" component={Welcome.Router} />
          <Route path={`/${config.clarityModule}/`} component={Clarity.Router} />
          {subscription.premium && <Route path={`/${config.identityModule}/`} component={Identity.Router} />}
          <Route path="/collaborate/:code/" component={Collaboration.Invited} />
          {cookies.get('signing-up') && !cookies.get('welcomed') && <Redirect to="/welcome/" />}
          {(config.development || config.uat) && <Route path="/test/" component={Test} />}
          {(config.development || config.uat) && <Route path="/test-repetition/" component={TestRepetition} />}
          <Redirect to="/today/" />
        </Switch>
      </ErrorBoundary>
      {isDesktop && (
        <Fragment>
          <div className={css('bottom-border')}>
            <Corner className={css.corner} />
          </div>
          <div className={css('right-border')}>
            <Corner className={css('corner', 'top-corner')} />
            <Corner className={css('corner', 'bottom-corner')} />
          </div>
        </Fragment>
      )}
      <Announcements.Manager />
    </div>
  ) : null;
}
